"use client";

import { useRouter } from "next/navigation";
import { useLanguage } from "@tillix/i18n/react";
import { Select } from "@tillix/ui-atoms/select";
import { useWglT } from "../i18n/use-t";
import {
  resolveToggleChange,
  TOGGLE_LANGUAGES,
  toToggleLanguage,
} from "./language-toggle-logic";

/**
 * English/Spanish switch for the WGL header. `setLanguage` updates the client
 * i18n context and the NEXT_LOCALE cookie; `router.refresh()` re-renders the
 * server components (order route back link, not-found page) with the cookie.
 */
export default function LanguageToggle() {
  const router = useRouter();
  const { language, setLanguage } = useLanguage();
  const t = useWglT();

  // Items-first so the trigger can render the selected label before hydration.
  const items = TOGGLE_LANGUAGES.map((code) => ({
    value: code,
    label: code === "es" ? t("language.es") : t("language.en"),
  }));

  const handleValueChange = (value: unknown) => {
    const next = resolveToggleChange(language, String(value));
    if (next === null) return;
    setLanguage(next);
    router.refresh();
  };

  return (
    <Select
      items={items}
      onValueChange={handleValueChange}
      value={toToggleLanguage(language)}
    >
      <Select.Trigger
        aria-label={t("header.languageLabel")}
        className="min-w-32"
        size="sm"
      >
        <Select.Value />
      </Select.Trigger>
      <Select.Popup>
        {items.map((item) => (
          <Select.Item key={item.value} value={item.value}>
            {item.label}
          </Select.Item>
        ))}
      </Select.Popup>
    </Select>
  );
}
